import { BossBars } from './scene/BossBars';
import { StormBadges } from './scene/StormBadges';
import { deckOf } from '@orb/sim';
import { loadout } from './game/loadout';
import { keepSolo } from './game/soloRecord';
import './ui/ui.css';
import * as THREE from 'three';
import { StaticBatch } from './engine/Batch';
import { startLoop } from './engine/Loop';
import { Perf } from './engine/Perf';
import { GpuClock } from './engine/GpuClock';
import { adapt } from './engine/adaptive';
import { measureFill } from './engine/FillMeter';
import { Profiler, type Variant } from './engine/Profiler';
import { Stage } from './engine/Stage';
import { loadTextures } from './engine/textures';
import { fxUniforms } from './fx/common';
import { Ambience } from './fx/Ambience';
import { setFakeLight, syncFakeLights } from './fx/fakeLights';
import { go, mountMenu, pageInvite, serverUrl, wayUrl } from './scene/Menu';
import { OPEN_WINDOWS, buyWithStars, invitedRoom, inviteLink, inviteUsed, loadTelegram, markInviteUsed, shareInvite, startShell } from './telegram';
import { Account } from './account';
import { AccessError, REFUSAL, Session } from './session';
import { rulesSeen, wideScreen } from './scene/rules';
import { boot as bootScreen } from './scene/Boot';
import { Screens } from './ui/screens';
import { Settings, systemReducedMotion } from './settings';
import { leaveMatch, seat } from './seat';
import { ERROR } from '@orb/protocol';
import { fogReach } from './scene/SpawnFog';
import { buildFlameCores } from './fx/FlameCores';
import { Fx } from './fx/Fx';
import { Glows } from './fx/Glows';
import { Particles } from './fx/Particles';
import { Board } from './game/Board';
import { Combat } from './game/Combat';
import { BOARD_COOP, BOARD_SOLO } from './game/config';
import { Driver } from './game/Driver';
import { NetGame } from '@orb/net';
import { mountNetStatus } from './scene/NetStatus';
import { GatePlate } from './scene/Gate';
import { SealDial } from './scene/SealDial';
import { mountAllyChip } from './scene/AllyChip';
import { Horde } from './game/Horde';
import { clawGeometry, stemGeo } from './game/Relic';
import { RelicPool } from './game/RelicPool';
import { createEnvironment, createMaterials } from './scene/materials';
import { buildWorld, type WorldCtx } from './scene/World';
import { Feedback } from './scene/Feedback';
import { SkillBar } from './scene/SkillBar';
import { TotemRack } from './scene/TotemRack';

async function boot() {
  const params = new URLSearchParams(location.search);
  // Opened as a Telegram Mini App: full screen, framed by the safe areas; the boot screen shows at once
  const tg = await loadTelegram();
  const shell = tg ? startShell(tg) : null;
  if (shell) requestAnimationFrame(() => requestAnimationFrame(() => shell.ready()));
  // Co-op is always online: ?play=coop creates a room, ?room=CODE joins one (the server decides the
  // mode, seed and seat; ?server= overrides its address). Anything else is an offline solo game.
  let net: NetGame | null = null;
  // a friend's invite (startapp=r_<code>) goes straight into that room — once per Telegram session
  // (marked used only when the server answers it; `via=invite` keeps the wording across a reload)
  let fromInvite = params.get('via') === 'invite';
  if (tg && !params.has('room') && !params.has('play')) {
    const invited = invitedRoom(tg);
    if (invited && !inviteUsed(invited)) {
      fromInvite = true; params.set('room', invited);
      const url = new URL(location.href); url.searchParams.set('room', invited); url.searchParams.set('via', 'invite'); history.replaceState(null, '', url);
    }
  }
  const account = new Account(serverUrl(params), tg?.initData);
  const session = new Session(account);
  const screens = new Screens();
  // Telegram's Back closes the top screen from the start (the battle sets its fuller handler once built)
  if (shell) shell.closeWindow = () => screens.back();
  // the player's settings: motion and vibration apply at once, quality when the scene is built
  const settings = new Settings();
  settings.watch((v) => { if (v.calm) document.documentElement.dataset.motion = 'reduced'; else delete document.documentElement.dataset.motion; });
  const wide = wideScreen();
  // an account going online without a nick chooses it first (Back cancels the way online)
  // the onboarding is its own chunk: a first visit, or the nick on the way online
  const onboardingChunk = () => import('./scene/Onboarding');
  session.askNick = async (s) => (await onboardingChunk()).askNick(screens, s, wide);
  // in Telegram the account comes with the launch (signed in at once, even for solo); on the web only
  // a stored session is resumed — a guest is made when one is needed, not on every visit
  const started = session.start();
  const online = params.has('room') || params.get('play') === 'coop' || params.get('play') === 'random';
  // «Грати з ботом» (offered when no ally turns up): offline co-op, the computer holds the ally's field
  const bot = !online && params.get('play') === 'bot';
  // offline the yard opens at rest under the main menu (its own chunk, loaded while the scene is);
  // online the match screens (search, room, «found») stand over it until the battle begins
  // «Грати ще» from a summary: straight into the next solo battle, no menu between
  const again = !online && !bot && takeAgain();
  const mainMenu = online || bot || again ? null : import('./scene/MainMenu');
  const matchScreens = online ? import('./scene/Matchmaking') : null;
  // fetched ahead, awaited later (where a failure is handled): not an unhandled rejection meanwhile
  void mainMenu?.catch(() => {}); void matchScreens?.catch(() => {});
  document.documentElement.dataset.scene = bot ? 'battle' : 'menu';
  // a first visit: four rules, then the nick, and the first battle (solo) waits until they are done;
  // the scene loads underneath meanwhile
  let welcome: Promise<unknown> | null = null;
  if (!online && !again) {
    const rules = !rulesSeen();
    if (rules || tg) await Promise.race([started, new Promise((r) => setTimeout(r, 1500))]);
    const nick = rules ? !(session.can('account') && session.nick !== null) : session.can('account') && session.nick === null;
    // a chunk that didn't load (the network, or a deploy replaced the files): the battle starts without it
    if (rules || nick) welcome = onboardingChunk().then((m) => m.onboarding({ screens, session, rules, nick, next: 'solo', wide })).catch((e) => console.error(e));
  }
  // online: the battle loads under «found» (or the room) and starts when both are done
  let sceneReady!: () => void;
  const ready = new Promise<void>((r) => (sceneReady = r));
  // a new match waits for «found» (the ally seated, the countdown done) before the battle begins
  let awaitFound = false, foundShown = false;
  let room: { close(): void } | null = null;
  if (online) {
    const joined = await joinOnline(params, session, screens, matchScreens!, wide, fromInvite);
    if (!joined) return;
    net = joined;
  }
  const coop = net ? net.room!.mode === 'coop' : bot;
  // a seat given back (a reload mid-match) goes straight on; a new match shows who came
  const resumed = !!net && net.room!.startTick > 0;
  // invites: in Telegram the Mini App link (startapp=r_<code>) through the native «Share» picker
  const invite = tg
    ? { link: (code: string) => inviteLink(code, account.appUrl), share: (code: string) => shareInvite(tg, inviteLink(code, account.appUrl)) }
    : { link: pageInvite };
  if (net && coop && !resumed) {
    const mm = await matchScreens!;
    const n = net, code = n.room!.code;
    // the battle begins as «found» leaves (the countdown done and the scene loaded)
    const showFound = (ally: string | undefined, title: string) => {
      foundShown = true;
      void mm.foundScreen(screens, { me: session.nick, ally: ally || 'Союзник', title, ready }).then(() => begin());
    };
    awaitFound = true;
    const random = params.get('play') === 'random', title = random ? 'Союзника знайдено' : 'Союзник у кімнаті';
    const ally = n.room!.players.find((p) => p.seat !== n.seat);
    if (ally) showFound(ally.name, title);
    else {
      // an own room waits for the friend; a random match's partner is being seated this very moment
      if (!random) room = mm.roomScreen(screens, { code, me: session.nick, invite, wide, onLeave: () => { leaveMatch(n); go({ play: 'solo' }); } });
      // a random partner being seated: say so at once, with a way out; one who never comes (closed the tab on
      // the way) is said to be late — the room won't start without them
      else {
        const wait = mm.lateAllyScreen(screens, { wide, onAgain: () => { leaveMatch(n); go({ play: 'random' }); }, onSolo: () => { leaveMatch(n); go({ play: 'solo' }); } });
        room = wait;
        window.setTimeout(() => { if (!foundShown) wait.late(); }, mm.ALLY_LATE_MS);
      }
      const prev = n.onMessage;
      n.onMessage = (m) => {
        prev(m);
        // the room's list learns the newcomer (the battle's labels and notices read it once loaded)
        if (m.type === 'player_joined' && !n.room!.players.some((p) => p.seat === m.player.seat)) n.room!.players.push(m.player);
        if (m.type === 'player_joined' && m.player.seat !== n.seat && !foundShown) { room?.close(); room = null; showFound(m.player.name, title); }
      };
    }
  }
  if (resumed) document.documentElement.dataset.scene = 'battle';
  const host = document.getElementById('stage')!;
  const stage = new Stage(host, settings.qualityOverride(params.get('q')), !coop);
  const Q = stage.quality;
  const { scene, camera } = stage;
  const tex = await loadTextures();
  scene.environment = createEnvironment(stage.renderer);
  scene.environmentIntensity = 0.65;

  const M = createMaterials(tex);
  const glows = new Glows(2048), floorGlows = new Glows(256, true), particles = new Particles(Q.particles);
  scene.add(glows.mesh, floorGlows.mesh, particles.mesh);
  const fx = new Fx(particles, scene, Q.flashLights);
  settings.watch((v) => { fx.motion = v.calm || systemReducedMotion() ? 0.35 : 1; });
  fx.density = Q.density;

  // the deterministic game; ?seed=N replays the same match
  const seed = net ? net.room!.seed : params.has('seed') ? Number(params.get('seed')) >>> 0 : (Math.random() * 4294967296) >>> 0;
  // online co-op: both seats are people; offline co-op keeps the computer ally
  const driver = net ? new Driver({ seed, mode: coop ? 'coop' : 'solo' }, { net, seat: net.room!.seat })
    : new Driver({ seed, mode: coop ? 'coop' : 'solo', players: bot ? [{}, { bot: true }] : undefined });
  const layout = coop ? BOARD_COOP : BOARD_SOLO;
  const ctx: WorldCtx = { scene, batch: new StaticBatch(), M, tex, glows, floorGlows, fires: [], dust: Q.dust, fogLite: Q.fogLite };
  const world = buildWorld(ctx, coop, layout);

  const ambience = new Ambience(particles, world.flames, Q.density);
  buildFlameCores(scene, world.flames);
  const key = world.key;
  key.shadow.mapSize.set(stage.shadowSize, stage.shadowSize);
  Object.assign(key.shadow.camera, { left: -8, right: 8, top: 10, bottom: -10, near: 1, far: 45 });
  key.shadow.bias = -0.0004; key.shadow.normalBias = 0.025;

  const pool = new RelicPool(scene, stemGeo, clawGeometry(), Q.tier);
  if (import.meta.env.DEV) checkLayout(world, driver);
  const board = new Board(driver, world, { scene, glows, floorGlows, fx, camera, pool }, fx, glows);
  const totemRack = new TotemRack(ctx, world, board, host, fx);
  // receivers on which the key light's shadow never lands (measured 0 px change) skip shadow sampling
  for (const mesh of ctx.batch.build(scene)) if (mesh.material === M.groove || mesh.material === M.trim) mesh.receiveShadow = false;
  const horde = new Horde(scene, M, Q.enemyDetail);
  horde.attach(driver.sim);
  if (!bot && !resumed) horde.presence = 0; // the yard at rest under the menu (or «found»)
  const combat = new Combat(driver, board, horde, fx, glows, floorGlows, scene, world.sealPos);
  const feedback = new Feedback(host, board);
  const abilities = combat.abilities;
  driver.follow(horde, board, abilities, combat);
  if (shell) driver.follow(shell.view(driver.me, () => settings.get('vibration')));
  const skillBar = new SkillBar(host, abilities, world.sealPos.z);
  // Stars purchases exist only inside Telegram (T5)
  const buy = tg ? (sku: string) => buyWithStars(tg, account, sku) : undefined;
  /* ---------- the battle menu (Esc, ☰, Telegram's Back): solo pauses, co-op goes on ---------- */
  const gameMenu = () => import('./scene/GameMenu');
  /** A lazy screen: a chunk that didn't load says so and offers a reload instead of a dead button. */
  const chunk = <T,>(load: () => Promise<T>) => load().catch((e) => { chunkFailed(e); return null; });
  let paused = false, menuOpen = false;
  const allyName = () => (bot ? 'Бот' : net?.room?.players.find((p) => p.seat !== net.seat)?.name || null);
  const openSettings = async () => (await chunk(gameMenu))?.settingsScreen(screens, {
    session, settings, autoTier: stage.autoTier, runningTier: Q.tier, canRestart: !net, inTelegram: !!tg, build: __BUILD__,
  });
  const openBattleMenu = async () => {
    if (menuOpen || held || over) return;
    menuOpen = true;
    // solo stops at once (the menu's chunk may still be on its way)
    paused = !net;
    // the battle's own interface steps back under the menu (as under the main menu)
    document.documentElement.dataset.scene = 'menu';
    abilities.cancel(); totemRack.close();
    const gm = await chunk(gameMenu);
    if (!gm) { paused = false; menuOpen = false; document.documentElement.dataset.scene = 'battle'; return; }
    gm.battleMenu(screens, {
      kind: net ? 'coop' : bot ? 'bot' : 'solo', ally: allyName(), wide,
      time: () => driver.sim.tick / 60,
      onSettings: () => void openSettings(),
      onRules: () => void gm.rulesScreen(screens, session, wide),
      onLeave: () => { if (net) leaveMatch(net); go({ play: 'solo' }); },
      onClose: () => { paused = false; menuOpen = false; document.documentElement.dataset.scene = 'battle'; },
    });
  };
  mountMenu(host, { net, onAlly: (name) => totemRack.setAllyName(name), onMenu: () => void openBattleMenu() });
  // Space in battle: the seal (as the skill has 1), unless a screen, a field or a button has the key
  window.addEventListener('keydown', (e) => {
    if (e.code !== 'Space' || e.repeat || e.ctrlKey || e.metaKey || e.altKey || held || paused || screens.size || document.querySelector(OPEN_WINDOWS)) return;
    if ((e.target as HTMLElement)?.closest?.('input, textarea, select, button, [contenteditable]')) return;
    e.preventDefault(); board.summonNow();
  });
  // Esc in battle: the skill's aim and the totem choice first (they close on Esc themselves), then the menu
  window.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || e.repeat || held || screens.size || abilities.targeting || document.querySelector(OPEN_WINDOWS)) return;
    e.preventDefault(); void openBattleMenu();
  }, true);
  if (net) mountNetStatus(host, net, invite.link);

  /* ---------- the gate and the end of the match («Брама впала») ---------- */
  const gate = new GatePlate(host, driver.sim, { coop, wide, seat: driver.me });
  const bossBars = new BossBars(host, horde);
  driver.follow(bossBars);
  const stormBadges = new StormBadges(host, board, () => driver.state.players[driver.me]?.storm ?? 0);
  const sealDial = new SealDial(host, () => driver.player, world.sealPos);
  if (coop) mountAllyChip(host, { net, name: allyName, totem: () => driver.state.players[1 - driver.me]?.totem ?? null });
  let over = false;
  // the record before this match (the summary's «Новий рекорд»), taken as the battle begins
  let bestBefore: number | null = null;
  const end = () => {
    if (over) return;
    over = true;
    // the ally's comings and goings no longer matter (the match is over for both)
    document.documentElement.dataset.over = '';
    // online the server replays to this tick: its verdict is the tally, and it closes the room
    let verdict: Promise<{ kills: number[]; bossKills: number[]; leaks: number } | null> | undefined;
    if (net) {
      const n = net, at = driver.sim.tick;
      verdict = new Promise((resolve) => {
        const prev = n.onMessage;
        const timer = setTimeout(() => resolve(null), 10000);
        n.onMessage = (m) => { prev(m); if (m.type === 'verdict' && m.tick >= at) { clearTimeout(timer); resolve(m); } };
        n.claim(driver.sim);
      });
    }
    abilities.cancel(); totemRack.close();
    shell?.setMatch(false, () => {});
    // the last breach is seen, the battle's own interface steps back, then the summary
    setTimeout(async () => {
      screens.clear(); paused = false; menuOpen = false;
      document.documentElement.dataset.scene = 'menu';
      const st = driver.sim.state.stats;
      // offline solo: its record is the device's own
      const soloBest = !net && !bot ? keepSolo(st.kills[driver.me] ?? 0, st.bossKills[driver.me] ?? 0) : null;
      const toMenu = () => { if (net) leaveMatch(net); go({ play: 'solo' }); };
      // the summary's chunk didn't load: the menu then (online the match is recorded all the same)
      const mod = await import('./scene/Summary').catch((e) => { console.error(e); return null; });
      if (!mod) { toMenu(); return; }
      mod.summaryScreen(screens, {
        kind: net ? 'coop' : bot ? 'bot' : 'solo', seat: driver.me, me: session.nick, ally: allyName(),
        seconds: driver.sim.tick / 60, local: { kills: [...st.kills], bossKills: [...st.bossKills], leaks: st.leaks },
        verdict, best: net ? bestBefore : null, soloBest, wide,
        rank: net ? (kills) => weeklyPlace(account, kills) : undefined,
        onAgain: () => { if (net) leaveMatch(net); if (net) go({ play: params.has('room') ? 'coop' : 'random' }); else if (bot) go({ play: 'bot' }); else { keepAgain(); go({ play: 'solo' }); } },
        onMenu: toMenu,
      });
    }, 1600);
  };
  driver.follow(gate, {
    events: (evs) => { if (evs.some((e) => e.type === 'gate_fallen')) end(); },
    resync: () => { if (driver.sim.over) end(); },
  });
  if (bot) totemRack.setAllyName('Бот');
  if (shell) {
    // Back closes the totem choice or a panel; a finished online match goes back to solo
    shell.closeWindow = () => {
      if (screens.back()) return true;
      const open = document.querySelector<HTMLElement>(OPEN_WINDOWS);
      if (open?.classList.contains('totem-choice')) { totemRack.close(); return true; }
      if (open) { open.hidden = true; return true; }
      if (document.querySelector('.coop-wait[role="alertdialog"]:not([hidden])')) { go({ play: 'solo' }); return true; }
      // in battle Back is the battle menu (the match is left from there, on purpose)
      if (!held) { void openBattleMenu(); return true; }
      return false;
    };
  }

  // Prepare instance transforms and shader programs before the first interactive frame.
  board.update(0, camera); totemRack.update(camera, 0); horde.render(0, 0);
  glows.flush(); floorGlows.flush(); particles.flush(); pool.flush(); fx.crystals.flush();
  stage.invalidateShadows();
  await stage.renderer.compileAsync(scene, camera);
  stage.render(0);

  /* ---------- input: pointer → ray → altar plane ---------- */
  const ray = new THREE.Raycaster(), ndc = new THREE.Vector2(), hit = new THREE.Vector3();
  const pickPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -(world.myAltar.cells[0].topY + 0.2));
  const roadPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  const project = (ev: PointerEvent, plane = pickPlane) => {
    const r = host.getBoundingClientRect();
    ndc.set(((ev.clientX - r.left) / r.width) * 2 - 1, -((ev.clientY - r.top) / r.height) * 2 + 1);
    ray.setFromCamera(ndc, camera);
    return ray.ray.intersectPlane(plane, hit);
  };
  let activePointer = -1;
  host.addEventListener('pointerdown', (ev) => {
    if (abilities.targeting && activePointer === -1) {
      if (project(ev, roadPlane)) abilities.confirm(hit.x, hit.z);
      return;
    }
    if (activePointer !== -1 || !project(ev)) return;
    board.hoverAt(-1, false);
    const totem = totemRack.pick(ray.ray);
    if (totem >= 0) { totemRack.activate(totem); return; }
    totemRack.close();
    if (board.pointerDown(hit.x, hit.z)) { activePointer = ev.pointerId; host.setPointerCapture(ev.pointerId); host.style.cursor = 'grabbing'; }
  });
  host.addEventListener('pointermove', (ev) => {
    if (activePointer === -1) {
      if (abilities.targeting) {
        if (project(ev, roadPlane)) abilities.aim(hit.x, hit.z);
        host.style.cursor = 'crosshair'; return;
      }
      if (ev.pointerType !== 'mouse' || !project(ev)) return;
      const i = board.cellAt(hit.x, hit.z), seal = board.sealHit(hit.x, hit.z);
      const totem = totemRack.pick(ray.ray); totemRack.hoverAt(totem);
      board.hoverAt(totem >= 0 ? -1 : i, totem < 0 && seal);
      host.style.cursor = totem >= 0 ? 'pointer' : i >= 0 && board.mine[i]?.state === 'idle' ? 'grab' : seal ? 'pointer' : 'default';
      return;
    }
    if (ev.pointerId !== activePointer) return;
    // coalesced events give sub-frame precision on high-rate input devices
    const evs = ev.getCoalescedEvents?.() ?? [ev];
    if (project(evs[evs.length - 1] ?? ev)) board.pointerMove(hit.x, hit.z);
    // a sphere carried over the shatter totem lights it
    totemRack.hoverAt(board.overTotem ? 0 : -1);
  });
  const up = (ev: PointerEvent) => {
    if (ev.pointerId !== activePointer) return;
    if (project(ev)) board.pointerMove(hit.x, hit.z);
    activePointer = -1;
    board.pointerUp(); totemRack.hoverAt(-1);
    host.style.cursor = 'default';
    if (host.hasPointerCapture(ev.pointerId)) host.releasePointerCapture(ev.pointerId);
  };
  const cancel = (ev: PointerEvent) => {
    if (ev.pointerId !== activePointer) return;
    activePointer = -1; board.pointerUp(true); host.style.cursor = 'default';
    if (host.hasPointerCapture(ev.pointerId)) host.releasePointerCapture(ev.pointerId);
  };
  host.addEventListener('pointerup', up);
  host.addEventListener('pointercancel', cancel);
  host.addEventListener('lostpointercapture', cancel);
  window.addEventListener('blur', () => {
    abilities.cancel();
    if (activePointer === -1) return;
    const id = activePointer;
    activePointer = -1; board.pointerUp(true); host.style.cursor = 'default';
    if (host.hasPointerCapture(id)) host.releasePointerCapture(id);
  });
  host.addEventListener('pointerleave', () => { board.hoverAt(-1, false); totemRack.hoverAt(-1); });
  host.addEventListener('contextmenu', (e) => e.preventDefault());

  /* ---------- shadows: re-render only when the board changes ---------- */
  let shadowTimer = 0;
  stage.invalidateShadows();

  // GPU frame time for the adaptive resolution (and the ?stats overlay); ?profile runs its own queries
  const clock = params.has('profile') ? null : new GpuClock(stage.renderer);
  stage.beyondBudget = !!clock?.available;
  let refreshMs = 1000 / 60;
  const perf = params.has('stats') && clock ? new Perf(stage.renderer, clock, () => `${stage.getDpr().toFixed(2)} / ${stage.maxDpr.toFixed(2)}  ${Q.tier}${Q.maxFps ? ' @' + Q.maxFps : ''}  fx ${fx.density.toFixed(2)}  msaa ${stage.composer.multisampling}\nbudget ${refreshMs.toFixed(1)} ms  gpu~ ${clock.ms.toFixed(2)}`) : null;
  // ?profile: measure each part of the frame on this GPU (see engine/Profiler.ts)
  let prof: Profiler | null = null;
  if (params.has('profile')) {
    const hide = (name: string, pick: (o: THREE.Object3D) => boolean): Variant => {
      const mats = new Set<THREE.Material>();
      scene.traverse((o) => { const m = (o as THREE.Mesh).material; if (m && pick(o)) (Array.isArray(m) ? m : [m]).forEach((x) => mats.add(x)); });
      return { name, off: () => mats.forEach((m) => (m.visible = false)), on: () => mats.forEach((m) => (m.visible = true)) };
    };
    const byName = (...names: string[]) => (o: THREE.Object3D) => names.includes(o.name);
    const matName = (...names: string[]) => (o: THREE.Object3D) => { const m = (o as THREE.Mesh).material as THREE.Material | undefined; return !!m && !Array.isArray(m) && names.includes(m.name); };
    // post-processing is not toggled here: switching composer passes skews GPU timings (recompiles/target swaps)
    const pointLights: THREE.Light[] = []; scene.traverse((o) => { if ((o as THREE.PointLight).isPointLight) pointLights.push(o as THREE.Light); });
    const receivers: THREE.Mesh[] = []; scene.traverse((o) => { if ((o as THREE.Mesh).isMesh && (o as THREE.Mesh).receiveShadow) receivers.push(o as THREE.Mesh); });
    const hordeMeshes = new Set<THREE.Object3D>(); (horde as unknown as { tpls: { meshes: THREE.Object3D[] }[] }).tpls.forEach((t) => t.meshes.forEach((m) => hordeMeshes.add(m)));
    const variants: Variant[] = [
      { name: `point lights (${pointLights.length})`, off: () => pointLights.forEach((l) => (l.visible = false)), on: () => pointLights.forEach((l) => (l.visible = true)) },
      { name: 'shadow receiving', off: () => receivers.forEach((m) => (m.receiveShadow = false)), on: () => receivers.forEach((m) => (m.receiveShadow = true)) },
      hide('enemies', (o) => hordeMeshes.has(o)),
      hide('particles', (o) => o === particles.mesh),
      hide('glow sprites', (o) => o === glows.mesh || o === floorGlows.mesh),
      hide('relic orbs + rings', (o) => [pool.orbs.mesh, pool.rings.mesh, pool.shards.mesh, pool.stems.mesh, pool.claws.mesh, pool.bands.mesh, pool.runes.mesh].includes(o as THREE.Mesh)),
      hide('spawn fog', byName('spawnFog')),
      hide('ash + dust', (o) => o.name === 'ash' || o === world.dust),
      hide('floor + road', matName('floor', 'road')),
      hide('ground fx', byName('venomPools', 'gravityWells', 'skillBurnGround', 'altarRunes', 'gateCracks')),
      hide('flames + portal', byName('flameCores', 'gatePortal')),
    ];
    prof = new Profiler(stage.renderer, variants);
  }
  // ?fill: per-object fill (screens shaded), GPU-independent — see engine/FillMeter.ts
  if (params.has('fill')) setTimeout(() => {
    const r = measureFill(stage.renderer, scene, stage.camera);
    console.log(`fill: total ${r.total} screens, opaque ${r.opaque}`); console.table(r.rows.slice(0, 25));
    const el = document.createElement('div');
    Object.assign(el.style, { position: 'fixed', right: '8px', top: '8px', padding: '8px 10px', font: '11px/1.4 ui-monospace,monospace', color: '#dfe', background: 'rgba(0,0,0,.72)', pointerEvents: 'none', whiteSpace: 'pre', zIndex: '20', borderRadius: '6px' });
    el.textContent = `fill ${r.total} screens (opaque ${r.opaque})\n` + r.rows.slice(0, 14).map((x) => `${x.raw.toFixed(2).padStart(5)} ${x.visible.toFixed(2).padStart(5)} ${x.kind} ${x.part.slice(0, 34)}`).join('\n');
    document.body.appendChild(el);
  }, 6000);
  const fixedDpr = params.get('dpr') ?? (params.has('profile') ? String(stage.getDpr()) : null);
  let lastDrop = 0;
  if (fixedDpr) stage.setDpr(+fixedDpr);

  /* ---------- loop ---------- */
  const shake = new THREE.Vector3(), breath = new THREE.Vector3();
  const layoutCam = new THREE.PerspectiveCamera();
  // camera breathing amount: ?sway=0 turns it off, ?sway=2 exaggerates it for tuning; reduced motion disables it
  const swayParam = Math.max(0, Number(params.get('sway') ?? 1));
  let fxTime = 0;
  // offline the battle waits for the menu's seal (or the first visit's «До бою»); meanwhile the
  // camera stands back, and comes down to the battle's framing as it begins
  let held = !bot && !resumed, pose = held ? 1 : 0;
  const lift = new THREE.Vector3();
  const begin = () => {
    held = false; document.documentElement.dataset.scene = 'battle';
    // the lobby's picks are the match's first actions (a seat given back mid-match keeps what it had)
    if (!resumed) {
      const pick = loadout(), me = driver.player;
      if (deckOf(pick.deck).join() !== me.deck.join()) driver.send({ type: 'set_deck', kinds: pick.deck });
      if (pick.skill !== me.skill) driver.send({ type: 'equip_skill', skill: pick.skill });
      if (pick.totem !== me.totem) driver.send({ type: 'equip_totem', kind: pick.totem });
    }
    bestBefore = session.profile?.progress.bestKills ?? null;
    // Telegram shows its Back button through the battle: it opens the battle menu
    shell?.setMatch(true, () => void openBattleMenu());
  };
  const handlers: Parameters<typeof startLoop>[0] = {
    step() {
      if (held || paused) return;
      // hit-stop and slow motion run the simulation's whole ticks less often
      driver.advance(fx.timeScale);
    },
    render(loopAlpha, dt, t) {
      const alpha = driver.alpha(loopAlpha, fx.timeScale);
      fogReach.value = stage.portrait * 1.8;
      perf?.begin(dt);
      fx.update(dt);
      const gdt = dt * Math.max(0.08, fx.timeScale);
      fxTime += gdt;
      particles.time = fxTime;
      fxUniforms.uTime.value = fxTime;

      ambience.update(gdt);
      board.update(gdt, camera);
      feedback.update();
      horde.render(alpha, gdt);
      gate.tick();
      combat.render(alpha, gdt);

      ctx.fires.forEach((f, i) => {
        f.l.intensity = f.base * (0.85 + 0.1 * Math.sin(t * 2.1 + f.ph) + 0.06 * Math.sin(t * 5.3 + f.ph * 2));
        if (i < 2) setFakeLight(i === 0 ? 0 : 2, f.l.position, f.l.distance, f.l.color, f.l.intensity);
      });
      world.smoke.forEach((q) => (q.t.offset.x = t * q.sp));

      // camera trauma shake (smooth noise, not white noise)
      const tr = fx.trauma * fx.trauma;
      shake.set(Math.sin(t * 37.1) + Math.sin(t * 23.3) * 0.5, Math.sin(t * 41.7 + 1) * 0.6, Math.sin(t * 29.9 + 2) + Math.sin(t * 17.1) * 0.5).multiplyScalar(tr * 0.12);
      // punch-in: dolly toward the focus point of the latest big event
      const pk = fx.punch * fx.punch;
      // idle "breathing": a very slow, small circle traced by the camera, the aim trailing it a little
      if (!held && pose > 0) pose = fx.motion < 1 ? 0 : Math.max(0, pose - dt / 1.4);
      const p = pose * pose * (3 - 2 * pose);
      // the horde rises out of the stone as the camera comes down
      horde.presence = held ? 0 : Math.min(1, horde.presence + dt / 1.1);
      horde.drawScale = 1 + 0.18 * stage.portrait;
      lift.subVectors(stage.camBase, stage.camTarget).multiplyScalar(0.3 * p);
      const sway = settings.get('breath') && fx.motion >= 1 ? swayParam : 0;
      const swayA = t * (Math.PI * 2 / 16), swayR = 0.06 * sway * (1 + p);
      breath.set(Math.cos(swayA) * swayR, Math.sin(swayA * 2) * swayR * 0.25, Math.sin(swayA) * swayR);
      camera.position.copy(stage.camBase).add(lift).lerp(fx.focus, pk * 0.12).add(shake).add(breath);
      camera.lookAt(stage.camTarget.x + shake.x * 0.5 + breath.x * 0.4 + (fx.focus.x - stage.camTarget.x) * pk * 0.15, stage.camTarget.y, stage.camTarget.z + shake.z * 0.5 + breath.z * 0.4 + (fx.focus.z - stage.camTarget.z) * pk * 0.15);
      camera.updateMatrixWorld();
      abilities.render(alpha, gdt, camera);
      // the skill medallions follow the steady framing, not the breathing or the shake, so they never jitter
      layoutCam.copy(camera); layoutCam.position.copy(stage.camBase); layoutCam.lookAt(stage.camTarget); layoutCam.updateMatrixWorld();
      skillBar.update(layoutCam, dt);
      // the bosses' bars follow the camera as drawn (its breathing and shake), over the warriors' heads
      bossBars.update(camera);
      sealDial.update(layoutCam);
      stormBadges.update(layoutCam);
      totemRack.update(layoutCam, gdt);
      stage.bloom.intensity = 1.35 + fx.bloomKick;

      // static scene: shadows are frozen; refresh only while relics move (+ a slow heartbeat)
      if (totemRack.consumeShadowChange() || board.busy || (shadowTimer -= dt) <= 0) { shadowTimer = 2; stage.invalidateShadows(); }

      glows.flush(); floorGlows.flush(); particles.flush(); pool.flush(); fx.crystals.flush();
      syncFakeLights(stage.camera);
      prof?.begin(); clock?.begin();
      stage.render(dt);
      clock?.end(); prof?.end();
      perf?.end(dt);
    },
    load(ratio, refresh) {
      refreshMs = refresh * 1000;
      if (fixedDpr) return;
      const now = performance.now();
      const r = adapt({ ratio, refresh, gpuMs: clock?.ms ?? NaN, dpr: stage.getDpr(), minDpr: stage.minDpr, maxDpr: stage.maxDpr, deviceDpr: devicePixelRatio,
        density: fx.density, maxDensity: Q.density, sinceDrop: now - lastDrop });
      if (r.dropped) lastDrop = now;
      if (r.dpr !== stage.getDpr()) stage.setDpr(r.dpr);
      fx.density = r.density;
    },
  };
  // ?fps=0 uncaps the render rate (e.g. to measure headroom), ?fps=N sets another cap
  const loop = startLoop(handlers, params.has('fps') ? Math.max(0, Number(params.get('fps'))) : Q.maxFps);
  // no frames while nobody can see them: minimised or behind another Telegram screen (solo holds
  // still, co-op catches up after), or a full screen of the interface covers the scene
  let hidden = false, covered = screens.size > 0 && !!screens.top?.covers;
  // online a covering screen (settings in co-op) doesn't stop the battle: it plays on, as the menu says,
  // instead of catching up in one burst afterwards
  const pause = () => loop.setPaused(hidden || (covered && !net));
  shell?.onActive((on) => { hidden = !on; pause(); });
  screens.onChange = (_open, c) => { covered = c; pause(); };
  pause();

  sceneReady();
  if (net ? !awaitFound : bot || again) begin();
  else if (welcome) void welcome.then(begin);
  else if (mainMenu) {
    try {
      const { mountMainMenu } = await mainMenu;
      mountMainMenu({
        screens, session, wide, onPlay: begin, onCoop: (way) => go(way), onSettings: () => void openSettings(),
        onProfile: async () => (await chunk(gameMenu))?.profileScreen(screens, { session, onSettings: () => void openSettings() }),
        onCollection: async () => (await chunk(() => import('./scene/Collection')))?.collectionScreen(screens, { wide }),
        onShop: async () => (await chunk(() => import('./scene/Collection')))?.shopScreen(screens, { session, buy }),
        onRanking: async () => (await chunk(() => import('./scene/Leaderboard')))?.leaderboardScreen(screens, session, { wide }),
      });
    } catch (e) { console.error(e); begin(); } // the menu didn't load (a dropped chunk): straight to solo
  }
  bootScreen.hide();
  if (import.meta.env.DEV) Object.assign(window, { driver, net, quality: Q, clock, measureFill, stage, board, horde, combat, abilities, skillBar, totemRack, fx, THREE, handlers });
}

/**
 * Our altar must stand exactly where the simulation puts our relics. (The ally's plinth across the
 * road is only a picture: in the sim every seat stands on the near altar's ground.)
 */
function checkLayout(world: ReturnType<typeof buildWorld>, driver: Driver) {
  const sim = driver.layout.altars[driver.me];
  world.myAltar.cells.forEach((c, i) => {
    const s = sim.cells[i];
    if (!s || Math.abs(c.x - s.x) > 1e-9 || Math.abs(c.z - s.z) > 1e-9 || Math.abs(c.topY - s.topY) > 1e-9) console.error('altar layout differs from the simulation at cell', i, c, s);
  });
}

/**
 * Connect and take a seat. A random ally is looked for on the search screen (the queue renewed
 * quietly when the server's minute runs out, until the player takes another way); a room or a code
 * shows its progress on the boot screen. On failure the boot screen explains and offers the ways on;
 * returns null then.
 */
async function joinOnline(params: URLSearchParams, session: Session, screens: Screens, screensModule: Promise<typeof import('./scene/Matchmaking')>, wide: boolean, fromInvite = false): Promise<NetGame | null> {
  const account = session.account;
  const random = params.get('play') === 'random' && !params.has('room');
  // an invite turned down (the nick cancelled, «Грати соло») is spent too: a reload in Telegram would bring it back
  const invited = fromInvite ? params.get('room') : null;
  const spend = () => { if (invited) markInviteUsed(invited); };
  const solo = wayUrl({ play: 'solo' });
  const offer = (text: string, links: [string, string][]) => bootScreen.offer(text, links.map(([label, href]) => ({ label, href, onClick: href === solo ? spend : undefined })));
  // a player: signed in (quietly a guest on a first visit, so the match counts towards the account)
  // and with a nick the partner will see — chosen now if the account has none
  bootScreen.status('вхід…');
  try { await session.require('player'); } catch (e) {
    // the nick's screen didn't load: not the server's fault — a reload brings the new files
    if (isChunkError(e)) { console.error(e); offer('Не вдалося завантажити частину гри — мабуть, її щойно оновили.', [['Оновити гру', location.href], ['Грати соло', solo]]); return null; }
    const why = e instanceof AccessError ? e.reason : 'offline';
    if (why === 'cancelled') { spend(); go({ play: 'solo' }); return null; }
    offer(why === 'relaunch' ? REFUSAL.relaunch : 'Сервер не відповідає.', [['Грати соло', solo], ...(why === 'offline' ? [['Спробувати ще', location.href] as [string, string]] : [])]);
    return null;
  }
  // the session lives as long as the tab: a reload of the same room (or a dropped connection) gets the
  // same seat back; any other room — a new one, a code, an invite — starts fresh
  const room = params.get('room');
  const stored = room ? seat.load(room) : null;
  let token: string | null = stored;
  const connect = () => {
    const net = new NetGame({ url: serverUrl(params), join: room ?? undefined, queue: random ? 'coop' : undefined, create: 'coop', session: stored, auth: account.token });
    net.link.onSession = (t) => { token = t; if (net.room) seat.keep(net.room.code, t); };
    return net;
  };
  // a random match may take up to the server's 60 s queue timeout; the rest should answer at once
  const attempt = (net: NetGame) => Promise.race([net.start().catch((e: Error) => e), new Promise<'timeout'>((r) => setTimeout(() => r('timeout'), random ? 75000 : 10000))]);

  let net = connect();
  let result: Awaited<ReturnType<typeof attempt>>;
  if (random) {
    const mm = await screensModule;
    let gone = false;
    // `leave`, not `cancel`: a room found that very moment frees its seat too (the partner isn't left waiting)
    const away = (to: Parameters<typeof go>[0]) => { gone = true; leaveMatch(net); go(to); };
    const search = mm.searchScreen(screens, {
      online: account.online(), wide,
      onCancel: () => away({ play: 'solo' }), onBot: () => away({ play: 'bot' }), onSolo: () => away({ play: 'solo' }),
    });
    for (;;) {
      result = await attempt(net);
      if (gone) return null;
      if (!(result instanceof Error) || net.errors.at(-1) !== ERROR.queue_timeout) break;
      // nobody in the server's minute: line up again (the screen has offered the bot by now)
      net.close(); net = connect();
    }
    // found (or failed): the search gives way to «found» (or the boot screen's explanation)
    search.stop();
    screens.close(search.spec);
  } else {
    bootScreen.status('з’єднання з сервером…');
    result = await attempt(net);
  }
  if (result instanceof Error || result === 'timeout') {
    net.close();
    const code = net.errors.at(-1);
    const gone = code === ERROR.room_not_found, full = code === ERROR.room_full;
    const why = result === 'timeout' ? 'Сервер не відповідає.'
      : gone ? (fromInvite ? 'Запрошення застаріло: кімнату вже закрито.' : 'Кімнату не знайдено: перевірте код.')
      : full ? 'У кімнаті вже двоє гравців.' : 'Не вдалося увійти в кімнату.';
    // the way on: search again, start an own room, or play solo
    // an invite the server answered (room gone or full) is spent; a server that didn't answer may try again
    if (fromInvite && room && (gone || full)) markInviteUsed(room);
    offer(why, [...(random ? [['Шукати ще', wayUrl({ play: 'random' })] as [string, string]] : []), ...(gone || full ? [['Новий кооп', wayUrl({ play: 'coop' })] as [string, string]] : []), ['Грати соло', solo]]);
    return null;
  }
  if (fromInvite && room) markInviteUsed(room);
  if (token) seat.keep(result.code, token);
  const url = new URL(location.href);
  url.searchParams.delete('play'); url.searchParams.set('room', result.code);
  history.replaceState(null, '', url);
  console.info(`[net] room ${result.code}, seat ${result.seat}, ${result.mode}`);
  return net;
}

/** A file of the game that didn't arrive: the network, or a deploy replaced the files under an open tab. */
const isChunkError = (e: unknown) => /dynamically imported module|Importing a module script failed|error loading dynamically imported/i.test(String((e as Error)?.message ?? e));
function chunkFailed(e: unknown) {
  console.error(e);
  bootScreen.offer('Не вдалося завантажити частину гри — мабуть, її щойно оновили.', [{ label: 'Оновити гру', onClick: () => location.reload() }, { label: 'Закрити', onClick: () => bootScreen.hide() }]);
}

boot().catch((e) => {
  if (isChunkError(e)) { console.error(e); bootScreen.offer('Не вдалося завантажити гру — мабуть, її щойно оновили.', [{ label: 'Оновити гру', onClick: () => location.reload() }]); return; }
  console.error(e);
  bootScreen.fail(String(e?.stack ?? e).slice(0, 400));
});

/** «Грати ще» after a solo summary: one reload straight into the battle (a flag for this tab only). */
const AGAIN = 'orb.again';
function keepAgain() { try { sessionStorage.setItem(AGAIN, '1'); } catch { /* the menu then */ } }
function takeAgain(): boolean {
  try { const v = sessionStorage.getItem(AGAIN) === '1'; sessionStorage.removeItem(AGAIN); return v; } catch { return false; }
}

/**
 * Our place this week once the finished match is recorded (the server verifies it in the background,
 * a few seconds): asked again until the board holds at least these kills, or we give up.
 */
async function weeklyPlace(account: Account, kills: number): Promise<number | null> {
  let rank: number | null = null;
  for (const wait of [1500, 3000, 5000, 8000]) {
    await new Promise((r) => setTimeout(r, wait));
    try {
      const me = (await account.leaderboard('week')).me;
      rank = me?.rank ?? null;
      if (rank !== null && me!.kills >= kills) return rank;
    } catch { /* offline for a moment: try again */ }
  }
  return rank;
}

